// F13: ① 物語解析 — チャンク抽出（map）→ コード集計・名寄せ（dedupe）→ 企画統合（reduce）→ analysis.json
// LLM 呼び出しは lib/llm-client.mjs から OpenRouter を直接利用。指示文は config/story-pipeline.json で管理。
import { randomBytes } from 'node:crypto';
import { llmConfig, callLlmJson, chunkText } from './llm-client.mjs';
import { listGames } from './games.mjs';
import { pipelineConfig, fillTemplate, toSnakeId, str, asArray } from './story-shared.mjs';
import {
    generationLanguageInstruction, inspectNovelSource, localizedForGeneration,
    normalizeOriginalNovelUrl, normalizeStorySubtitle, withRequiredTranslationPolicy,
} from './story-source.mjs';
import { readNovel, readProjectMeta, saveAnalysis, recordStep, addUsage } from './story-project.mjs';
import { auditLog } from './logger.mjs';

const DEFAULT_COLORS = { main: '#1a1a2e', accent: '#e94560', gold: '#f5a623' };

export async function runAnalyze(projectId, { onLog = () => {}, signal = null } = {}) {
    const meta = readProjectMeta(projectId);
    const novel = readNovel(projectId);
    const source = inspectNovelSource(novel);
    const cfg = pipelineConfig().analyze;
    const chunks = chunkText(novel, llmConfig().chunk);
    onLog(`物語 ${novel.length.toLocaleString()}文字を ${chunks.length} チャンクに分割して抽出します（生成言語: ${source.generationLanguage === 'ja' ? '日本語' : '英語'} / 原作URL: ${source.originalUrl ? '検出' : 'なし'}）`);

    const extracted = [];
    let failedChunks = 0;
    let lastChunkError = null;
    for (const [i, chunk] of chunks.entries()) {
        signal?.throwIfAborted(); // 中断チェック（キリの良い区切り＝チャンク境界。ここで止めれば analysis 未保存で安全）
        const prompt = fillTemplate(cfg.extractPrompt, {
            chunkIndex: i + 1,
            chunkCount: chunks.length,
            chunkText: chunk,
            generationLanguageInstruction: generationLanguageInstruction(source.generationLanguage),
        });
        try {
            const r = await callLlmJson('extract', { system: cfg.extractSystem, prompt, onLog });
            extracted.push(r.json ?? {});
            addUsage(projectId, r.usage, r.calls);
            onLog(`  チャンク ${i + 1}/${chunks.length}: 抽出完了（人物 ${asArray(r.json?.characters).length} / 場所 ${asArray(r.json?.locations).length} / 敵 ${asArray(r.json?.enemies).length}）`);
        } catch (e) {
            failedChunks++;
            lastChunkError = e;
            onLog(`  ⚠ チャンク ${i + 1}/${chunks.length}: 抽出失敗（${String(e.message).slice(0, 120)}）— スキップして続行`);
            // キー未設定・無効・残高切れは以降のチャンクも全て失敗するため即中断する
            if (['token_missing', 'openrouter_auth', 'openrouter_credits'].includes(e.code)) throw e;
        }
    }
    if (extracted.length === 0) {
        // 原因別に「次にやるべきこと」を変える: キー系はここに来る前に即中断・rethrow されるため、
        // ここに来るのはモデル側の問題（空応答・規制・タイムアウト等）＝API設定を疑わせる誤誘導をしない
        const cause = String(lastChunkError?.message ?? '不明なエラー').slice(0, 160);
        const hint = ['token_missing', 'openrouter_auth', 'openrouter_credits'].includes(lastChunkError?.code)
            ? '「API設定」画面で OpenRouter キーの登録と接続テストを確認してください。'
            : 'API キーと接続は有効です。モデル側の一時的な問題の可能性が高いため、時間をおいて「① 解析」を再実行してください。';
        throw new Error(`全チャンクの抽出に失敗しました（原因: ${cause}）。${hint}`);
    }

    const digest = mergeExtracted(extracted, cfg.limits ?? {});
    onLog(`集計: 人物 ${digest.characters.length} / 場所 ${digest.locations.length} / 建物 ${digest.buildings.length} / 敵 ${digest.enemies.length} / アイテム ${digest.items.length} / スキル ${digest.skills.length} / イベント ${digest.events.length}`);

    signal?.throwIfAborted(); // 中断チェック（reduce の前。ここまで未保存なので止めても安全に再実行可能）
    onLog('企画統合（reduce）を実行します……');
    const reducePrompt = fillTemplate(cfg.reducePrompt, {
        fileName: meta.novel?.fileName ?? '(無題)',
        opening: novel.slice(0, 600).replace(/\s+/g, ' '),
        digest: JSON.stringify(capDigest(digest), null, 1),
        startWorldBaseCatalog: JSON.stringify(
            (pipelineConfig().startWorldBases?.options ?? []).map(({ id, label, hint, sky, defaultWeather, forcedWeather, strengths, limitations, foundationFills }) => ({
                id, label, hint, sky, defaultWeather, forcedWeather, strengths, limitations, foundationFills,
            })),
            null,
            1,
        ),
        generationLanguageInstruction: generationLanguageInstruction(source.generationLanguage),
    });
    const r = await callLlmJson('reduce', { system: cfg.reduceSystem, prompt: reducePrompt, onLog });
    addUsage(projectId, r.usage, r.calls);

    const analysis = normalizeAnalysis(r.json ?? {}, source);
    saveAnalysis(projectId, analysis);
    recordStep(projectId, 'analyze', { ok: true, chunks: chunks.length, failedChunks, model: r.model });
    auditLog('story-analyze', { projectId, chunks: chunks.length, failedChunks, cast: analysis.cast.length });
    onLog(`✔ 解析完了: 「${analysis.game.title.ja}」 gameId=${analysis.game.gameId} / startMap=${analysis.game.startMapId} / cast ${analysis.cast.length}体`);
    return analysis;
}

/** チャンク抽出結果を名寄せ・出現回数集計し、上限で絞り込む。 */
export function mergeExtracted(results, limits) {
    const digest = { characters: [], locations: [], buildings: [], items: [], skills: [], enemies: [], events: [] };
    const mergeFields = ['role', 'appearance', 'personality', 'speech', 'kind', 'description', 'purpose', 'location', 'user', 'habitat', 'enemyRank'];
    for (const cat of ['characters', 'locations', 'buildings', 'items', 'skills', 'enemies']) {
        const map = new Map();
        for (const r of results) {
            for (const item of asArray(r?.[cat])) {
                const name = str(item?.name);
                if (!name) continue;
                const key = name.replace(/\s+/g, '');
                if (!map.has(key)) map.set(key, { name, mentions: 0, humanoidVotes: 0, humanoidTotal: 0 });
                const cur = map.get(key);
                cur.mentions++;
                for (const f of mergeFields) {
                    const v = str(item?.[f]);
                    if (v && v.length > str(cur[f]).length) cur[f] = v; // 最も具体的（長い）描写を採用
                }
                if (typeof item?.humanoid === 'boolean') {
                    cur.humanoidTotal++;
                    if (item.humanoid) cur.humanoidVotes++;
                }
            }
        }
        digest[cat] = [...map.values()]
            .sort((a, b) => b.mentions - a.mentions)
            .slice(0, limits[cat] ?? 20)
            .map(({ humanoidVotes, humanoidTotal, ...rest }) => {
                if (cat !== 'characters' && cat !== 'enemies') return rest;
                const humanoid = humanoidTotal > 0 ? humanoidVotes * 2 >= humanoidTotal : cat === 'characters';
                return { ...rest, humanoid };
            });
    }
    // イベントは名寄せせず物語の進行順を維持して重複だけ除去
    const seen = new Set();
    for (const r of results) {
        for (const ev of asArray(r?.events)) {
            const summary = str(ev?.summary);
            if (!summary || seen.has(summary)) continue;
            seen.add(summary);
            digest.events.push({ summary, location: str(ev?.location) });
        }
    }
    digest.events = digest.events.slice(0, limits.events ?? 24);
    return digest;
}

/** reduce へ渡す集計 JSON をトークン予算内（文字数上限）に収める。 */
function capDigest(digest, maxChars = 22000) {
    let d = digest;
    for (let i = 0; JSON.stringify(d).length > maxChars && i < 6; i++) {
        d = Object.fromEntries(
            Object.entries(d).map(([k, v]) => [k, Array.isArray(v) ? v.slice(0, Math.max(3, Math.ceil(v.length * 0.7))) : v]),
        );
    }
    return d;
}

/**
 * gameId をサーバ全体でユニークになるようランダム接尾辞つきで確定する（2026-07-07 ユーザー要件）。
 * 本ツールで作ったゲームは最終的に共通サーバで公開されるため、LLM が付けた素の ID（例: clockwork_city）は
 * 他の利用者・他の作品と衝突し得る → 必ず `_` + 4 文字（a-z0-9）を付与し、ローカル既存ゲームとも衝突チェックする。
 * 既に接尾辞形式（_xxxx）の ID はそのまま（再解析で ID が変わらないように）。
 */
export function uniqueGameId(baseId) {
    // 接尾辞は「必ず数字を 1 文字以上含む 4 文字」= 英単語（_city, _port 等）と誤判定しない
    if (/_(?=[a-z0-9]{4}$)[a-z0-9]*[0-9][a-z0-9]*$/.test(baseId)) return baseId;
    const existing = new Set(listGames().map((g) => g.gameId));
    for (let i = 0; i < 20; i++) {
        const chars = Array.from(randomBytes(4), (b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36]);
        chars[randomBytes(1)[0] % 4] = '0123456789'[randomBytes(1)[0] % 10]; // 数字を必ず 1文字混ぜる
        const candidate = `${baseId}_${chars.join('')}`;
        if (!existing.has(candidate)) return candidate;
    }
    return `${baseId}_${Date.now().toString(10).slice(-4)}`;
}

/**
 * 設定案ダイアログで編集可能な表示・作品情報だけを反映する。
 * gameId・開始マップ・キャラID等の構造値は「解析結果を編集」側でのみ変更させる。
 */
export function mergeEditableGameSettings(currentAnalysis, requestedAnalysis) {
    const next = JSON.parse(JSON.stringify(currentAnalysis ?? {}));
    const currentGame = next.game ?? (next.game = {});
    const requestedGame = requestedAnalysis?.game ?? {};
    for (const field of ['title', 'description']) {
        if (requestedGame[field] && typeof requestedGame[field] === 'object' && !Array.isArray(requestedGame[field])) {
            currentGame[field] = {
                ...(currentGame[field] ?? {}),
                ja: requestedGame[field].ja,
                en: requestedGame[field].en,
            };
        }
    }
    for (const field of ['subtitle', 'originalUrl', 'genre', 'worldSetting']) {
        if (Object.prototype.hasOwnProperty.call(requestedGame, field)) currentGame[field] = requestedGame[field];
    }
    return next;
}

/** reduce の出力を検証・正規化して analysis.json の確定形にする（ID は ASCII snake_case 強制）。 */
export function normalizeAnalysis(raw, sourceContext = {}) {
    const game = raw?.game ?? {};
    const hasSourceLanguageOverride = Object.prototype.hasOwnProperty.call(sourceContext, 'sourceLanguage');
    const hasGenerationLanguageOverride = Object.prototype.hasOwnProperty.call(sourceContext, 'generationLanguage');
    const sourceLanguage = (hasSourceLanguageOverride ? sourceContext.sourceLanguage : (game.sourceLanguage ?? 'ja')) === 'ja' ? 'ja' : 'en';
    const generationLanguage = (hasGenerationLanguageOverride ? sourceContext.generationLanguage : (game.generationLanguage ?? sourceLanguage)) === 'ja' ? 'ja' : 'en';
    const rawTitleJa = str(game.title?.ja);
    const rawTitleEn = str(game.title?.en);
    const title = generationLanguage === 'ja'
        ? { ja: rawTitleJa || rawTitleEn || '無題の物語', en: rawTitleEn || rawTitleJa || 'Untitled Story' }
        : { ja: rawTitleJa || rawTitleEn || 'Untitled Story', en: rawTitleEn || rawTitleJa || 'Untitled Story' };
    const description = {
        ja: str(game.description?.ja) || (generationLanguage === 'ja' ? `${title.ja}の世界を冒険する 3D ボクセル RPG` : str(game.description?.en) || `A voxel RPG based on ${title.en}`),
        en: str(game.description?.en) || (generationLanguage === 'en' ? str(game.description?.ja) || `A voxel RPG based on ${title.en}` : `A voxel RPG based on ${title.en}`),
    };
    const subtitle = normalizeStorySubtitle(game.subtitle, localizedForGeneration(description, generationLanguage, localizedForGeneration(title, generationLanguage)));
    const hasOriginalUrlOverride = Object.prototype.hasOwnProperty.call(sourceContext, 'originalUrl');
    const originalUrl = normalizeOriginalNovelUrl(
        hasOriginalUrlOverride ? sourceContext.originalUrl : game.originalUrl,
        generationLanguage,
    );
    const gameId = uniqueGameId(toSnakeId(game.gameId ?? title.en, 'my_story_game'));
    const startMapId = toSnakeId(game.startMapId ?? raw?.world?.startMap?.mapId, 'start_town');
    // 開始ワールドの土台（マイワールド 6 種）: 物語に合う土台を LLM が選ぶ。未知値は config の既定へ
    const baseCfg = pipelineConfig().startWorldBases ?? {};
    const validBases = new Set((baseCfg.options ?? []).map((o) => o.id));
    const startWorldBase = validBases.has(game.startWorldBase) ? game.startWorldBase : (baseCfg.default ?? 'bottom_world');
    const protagonist = game.protagonist === 'girl' ? 'girl' : 'boy';
    const colorOf = (v, fallback) => (/^#[0-9a-fA-F]{6}$/.test(str(v)) ? str(v).toLowerCase() : fallback);
    const colors = {
        main: colorOf(game.colors?.main, DEFAULT_COLORS.main),
        accent: colorOf(game.colors?.accent, DEFAULT_COLORS.accent),
        gold: colorOf(game.colors?.gold, DEFAULT_COLORS.gold),
    };
    const charOf = (slot, defId, defJa, defEn) => {
        const c = game.characters?.[slot] ?? {};
        return {
            id: toSnakeId(c.id ?? c.name?.en, defId),
            name: { ja: str(c.name?.ja) || defJa, en: str(c.name?.en) || defEn },
            basedOn: str(c.basedOn) || null,
        };
    };
    const characters = {
        boy: charOf('boy', 'boy', '少年', 'Boy'),
        girl: charOf('girl', 'girl', '少女', 'Girl'),
    };
    if (characters.boy.id === characters.girl.id) characters.girl.id = `${characters.girl.id}_g`;

    const summaryKeys = ['title', 'protagonist', 'playable', 'maps', 'buildings', 'npcs', 'enemies', 'items', 'skills', 'events', 'audio', 'translation'];
    const summary = Object.fromEntries(summaryKeys.map((k) => [k, str(raw?.summary?.[k])]));
    if (!summary.title) summary.title = `${title.ja} / ${title.en}`;
    if (!summary.audio) summary.audio = '既存音源（sevenfold 収録 BGM/SE）を流用する。';
    summary.translation = withRequiredTranslationPolicy(
        summary.translation || `英語タイトルは「${title.en}」。固有名詞は作品内の表記で統一する。`,
        generationLanguage,
    );

    const rawContentRules = raw?.contentRules ?? {};
    const magic = ['present', 'absent', 'uncertain'].includes(rawContentRules.magic)
        ? rawContentRules.magic
        : 'uncertain';
    const technology = ['primitive', 'preindustrial', 'modern', 'futuristic', 'mixed', 'uncertain'].includes(rawContentRules.technology)
        ? rawContentRules.technology
        : 'uncertain';
    const contentRules = {
        magic,
        technology,
        itemStyle: str(rawContentRules.itemStyle),
        skillStyle: str(rawContentRules.skillStyle),
        evidence: asArray(rawContentRules.evidence).map(str).filter(Boolean).slice(0, 8),
    };

    const cast = [];
    const usedIds = new Set();
    const enemyScaleCfg = pipelineConfig().enemyVisualScale ?? {};
    const allowedEnemyRanks = new Set(asArray(enemyScaleCfg.allowedRanks));
    const defaultEnemyRank = allowedEnemyRanks.has(enemyScaleCfg.defaultRank)
        ? enemyScaleCfg.defaultRank
        : asArray(enemyScaleCfg.allowedRanks)[0];
    for (const [i, c] of asArray(raw?.cast).entries()) {
        const primaryName = localizedForGeneration(c?.name, generationLanguage, str(c?.name));
        const nameJa = str(c?.name?.ja) || primaryName || `キャラ${i + 1}`;
        const nameEn = str(c?.name?.en) || primaryName || `cast_${i + 1}`;
        let id = toSnakeId(c?.id ?? c?.name?.en ?? nameJa, `cast_${i + 1}`);
        while (usedIds.has(id)) id = `${id}2`;
        usedIds.add(id);
        const role = ['protagonist', 'partner', 'npc', 'enemy'].includes(c?.role) ? c.role : 'npc';
        const enemyRank = allowedEnemyRanks.has(c?.enemyRank) ? c.enemyRank : defaultEnemyRank;
        const aiPersona = role === 'protagonist' || role === 'partner' ? normalizeCastAiPersona(c?.aiPersona) : null;
        cast.push({
            id,
            role,
            name: { ja: nameJa, en: nameEn || id },
            appearance: str(c?.appearance),
            personality: str(c?.personality),
            humanoid: c?.humanoid !== false,
            locationHint: str(c?.locationHint),
            ...(role === 'enemy' && enemyRank ? { enemyRank } : {}),
            ...(aiPersona ? { aiPersona } : {}),
        });
    }
    fixPlayableRoles(cast, characters, protagonist, usedIds);

    const sm = raw?.world?.startMap ?? {};
    const world = {
        startMap: {
            mapId: startMapId,
            name: {
                ja: str(sm.name?.ja) || localizedForGeneration(sm.name, generationLanguage, title.ja),
                en: str(sm.name?.en) || localizedForGeneration(sm.name, generationLanguage, title.en),
            },
            concept: str(sm.concept),
            terrainHint: str(sm.terrainHint),
            areas: asArray(sm.areas).slice(0, 6).map((a, i) => ({
                id: toSnakeId(a?.id ?? a?.name, `area_${i + 1}`),
                name: str(a?.name) || (generationLanguage === 'ja' ? `エリア${i + 1}` : `Area ${i + 1}`),
                purpose: str(a?.purpose),
            })),
            buildings: asArray(sm.buildings).slice(0, 8).map((b, i) => ({
                id: toSnakeId(b?.id ?? b?.name, `building_${i + 1}`),
                name: str(b?.name) || (generationLanguage === 'ja' ? `建物${i + 1}` : `Building ${i + 1}`),
                purpose: str(b?.purpose),
            })),
        },
        futureMaps: asArray(raw?.world?.futureMaps).slice(0, 10).map((m, i) => ({
            mapId: toSnakeId(m?.mapId ?? m?.name, `map_${i + 1}`),
            name: str(m?.name),
            concept: str(m?.concept),
        })),
    };

    return {
        formatVersion: '1.0',
        analyzedAt: new Date().toISOString(),
        game: {
            gameId, title, description, subtitle, originalUrl, sourceLanguage, generationLanguage,
            genre: str(game.genre) || (generationLanguage === 'ja' ? 'ファンタジー' : 'Fantasy'),
            worldSetting: str(game.worldSetting), colors, protagonist, characters, startMapId, startWorldBase,
        },
        contentRules,
        summary,
        cast,
        world,
    };
}

// AI チャット NPC（人格BOT）ペルソナの許可フィールド。llm-hub /api/npc-chat の
// NPC_CHAT_LIMITS（persona 全体 2,000 字・フィールド 400 字・リスト項目 200 字）を
// 生成側で確実に下回るよう、抽出時点で件数・文字数をクランプする。
const AI_PERSONA_STRING_FIELDS = ['role', 'personality', 'speechStyle', 'firstPerson', 'secondPerson', 'attitude', 'goals'];
// greetings は companion の固定会話（既定10件のユーザー要件・2026-07-17）のため上限12件まで保持する
const AI_PERSONA_LIST_FIELDS = [['knowledge', 6], ['taboos', 4], ['fallbackLines', 4], ['greetings', 12]];
const AI_PERSONA_STRING_MAX_CHARS = 120;
const AI_PERSONA_LIST_ITEM_MAX_CHARS = 100;

/**
 * ①解析（reduce）が出力した cast[].aiPersona を許可フィールドだけへ正規化する。
 * プレイアブル 2 キャラ（protagonist / partner）の相方 AI チャット NPC 用データ。
 * 有効なフィールドが 1 つも無ければ null（＝⑤側が既定ペルソナを合成する）。
 */
export function normalizeCastAiPersona(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const persona = {};
    for (const field of AI_PERSONA_STRING_FIELDS) {
        const value = str(raw[field]).slice(0, AI_PERSONA_STRING_MAX_CHARS);
        if (value) persona[field] = value;
    }
    for (const [field, maxItems] of AI_PERSONA_LIST_FIELDS) {
        const values = asArray(raw[field])
            .map((item) => str(item).slice(0, AI_PERSONA_LIST_ITEM_MAX_CHARS))
            .filter(Boolean)
            .slice(0, maxItems);
        if (values.length > 0) persona[field] = values;
    }
    return Object.keys(persona).length > 0 ? persona : null;
}

/** cast に protagonist / partner がちょうど 1 人ずつ存在する状態へ補正する。 */
function fixPlayableRoles(cast, characters, protagonist, usedIds) {
    const protagonistChar = protagonist === 'girl' ? characters.girl : characters.boy;
    const partnerChar = protagonist === 'girl' ? characters.boy : characters.girl;
    for (const [role, ch, insertAt] of [
        ['protagonist', protagonistChar, 0],
        ['partner', partnerChar, 1],
    ]) {
        const matches = cast.filter((c) => c.role === role);
        for (const extra of matches.slice(1)) extra.role = 'npc';
        if (matches.length === 0) {
            let id = ch.id;
            while (usedIds.has(id)) id = `${id}2`;
            usedIds.add(id);
            cast.splice(insertAt, 0, {
                id,
                role,
                name: { ...ch.name },
                appearance: '',
                personality: '',
                humanoid: true,
                locationHint: '',
            });
        }
    }
}
